// Staging model for a quote's child records (additional charges + stopoffs).
//
// Quotes are Save-button-only: editing a charge rate, adding a stopoff, etc.
// must NOT hit the server immediately. Each host (QuoteDetailPage, the lane
// drawer) keeps a local draft array for display PLUS an ordered queue of the
// pending operations here. The queue reconciles in-session churn (add-then-edit,
// add-then-delete, repeated edits) so Save replays the minimal set of server
// calls. Temp rows carry a negative id until Save assigns a real recno.
import type { StopoffFieldsPayload } from '../../services/quote.service';
import type { StopoffRow } from './fixtures';

export interface ChargeAddPayload {
  additionalchargesrecno: number;
  addlchargeratetype?: string;
  addlchargerate?: number;
  freetime?: number;
  freetimeunit?: string;
  additionaltype?: string;
  additionaldays?: number;
  tablename?: 'directquote' | 'transloadquote' | 'deliveryquote';
  tablerecno?: number;
}
export interface ChargePatch {
  addlchargeratetype?: string;
  addlchargerate?: number;
  freetime?: number;
  freetimeunit?: string;
  additionaltype?: string;
  additionaldays?: number;
}

export type ChargeOp =
  | { kind: 'add'; tempId: number; payload: ChargeAddPayload }
  | { kind: 'update'; id: number; payload: ChargePatch }
  | { kind: 'delete'; id: number };

export type StopoffOp =
  | { kind: 'add'; tempId: number; payload: StopoffFieldsPayload }
  | { kind: 'update'; id: number; payload: StopoffFieldsPayload }
  | { kind: 'delete'; id: number };

// ── Charge op staging ──────────────────────────────────────────────────────
export const stageChargeAdd = (ops: ChargeOp[], tempId: number, payload: ChargeAddPayload): ChargeOp[] =>
  [...ops, { kind: 'add', tempId, payload }];

export function stageChargeUpdate(ops: ChargeOp[], id: number, patch: ChargePatch): ChargeOp[] {
  // Editing a not-yet-saved row folds into its pending add (still one add on Save).
  if (id < 0) {
    return ops.map((op) =>
      op.kind === 'add' && op.tempId === id ? { ...op, payload: { ...op.payload, ...patch } } : op);
  }
  // Repeated edits to the same saved row collapse into one update.
  if (ops.some((op) => op.kind === 'update' && op.id === id)) {
    return ops.map((op) =>
      op.kind === 'update' && op.id === id ? { ...op, payload: { ...op.payload, ...patch } } : op);
  }
  return [...ops, { kind: 'update', id, payload: patch }];
}

export function stageChargeDelete(ops: ChargeOp[], id: number): ChargeOp[] {
  // A row that only ever existed in the draft → drop its pending add; nothing to persist.
  if (id < 0) return ops.filter((op) => !(op.kind === 'add' && op.tempId === id));
  // Drop any pending edits for the row, then record the delete.
  return [...ops.filter((op) => !(op.kind === 'update' && op.id === id)), { kind: 'delete', id }];
}

// ── Stopoff op staging ─────────────────────────────────────────────────────
export const stageStopoffAdd = (ops: StopoffOp[], tempId: number, payload: StopoffFieldsPayload): StopoffOp[] =>
  [...ops, { kind: 'add', tempId, payload }];

export function stageStopoffUpdate(ops: StopoffOp[], id: number, payload: StopoffFieldsPayload): StopoffOp[] {
  if (id < 0) {
    return ops.map((op) =>
      op.kind === 'add' && op.tempId === id ? { ...op, payload: { ...op.payload, ...payload } } : op);
  }
  if (ops.some((op) => op.kind === 'update' && op.id === id)) {
    return ops.map((op) =>
      op.kind === 'update' && op.id === id ? { ...op, payload: { ...op.payload, ...payload } } : op);
  }
  return [...ops, { kind: 'update', id, payload }];
}

export function stageStopoffDelete(ops: StopoffOp[], id: number): StopoffOp[] {
  if (id < 0) return ops.filter((op) => !(op.kind === 'add' && op.tempId === id));
  return [...ops.filter((op) => !(op.kind === 'update' && op.id === id)), { kind: 'delete', id }];
}

// ── Display-row mappers (StopoffFieldsPayload → StopoffRow) ─────────────────
export function stopoffRowFromPayload(p: StopoffFieldsPayload, stopId: number, index: number): StopoffRow {
  return {
    stopId,
    stopNum: p.stopnum ?? index + 1,
    stopName: p.stopname ?? '',
    addr1: p.stopadd1 ?? '',
    addr2: p.stopadd2 ?? '',
    city: p.stopcity ?? '',
    state: p.stopstate ?? '',
    zip: p.stopzip ?? '',
    stopType: p.stoptype ?? '',
  };
}

export function stopoffRowPatch(p: StopoffFieldsPayload): Partial<StopoffRow> {
  const patch: Partial<StopoffRow> = {};
  if (p.stopnum !== undefined) patch.stopNum = p.stopnum;
  if (p.stopname !== undefined) patch.stopName = p.stopname;
  if (p.stopadd1 !== undefined) patch.addr1 = p.stopadd1;
  if (p.stopadd2 !== undefined) patch.addr2 = p.stopadd2;
  if (p.stopcity !== undefined) patch.city = p.stopcity;
  if (p.stopstate !== undefined) patch.state = p.stopstate;
  if (p.stopzip !== undefined) patch.zip = p.stopzip;
  if (p.stoptype !== undefined) patch.stopType = p.stoptype;
  return patch;
}

// ── Replay (on Save) ───────────────────────────────────────────────────────
export interface ChargeReplaySvc {
  addCharge(qn: number, rev: number, payload: ChargeAddPayload): Promise<unknown>;
  updateCharge(qn: number, rev: number, id: number, payload: ChargePatch): Promise<unknown>;
  deleteCharge(qn: number, rev: number, id: number): Promise<unknown>;
}
export async function replayChargeOps(svc: ChargeReplaySvc, qn: number, rev: number, ops: ChargeOp[]): Promise<void> {
  for (const op of ops) {
    if (op.kind === 'add') await svc.addCharge(qn, rev, op.payload);
    else if (op.kind === 'update') await svc.updateCharge(qn, rev, op.id, op.payload);
    else await svc.deleteCharge(qn, rev, op.id);
  }
}

export interface StopoffReplaySvc {
  addStopoff(qn: number, rev: number, payload: StopoffFieldsPayload): Promise<unknown>;
  updateStopoff(qn: number, rev: number, id: number, payload: StopoffFieldsPayload): Promise<unknown>;
  deleteStopoff(qn: number, rev: number, id: number): Promise<unknown>;
}
export async function replayStopoffOps(svc: StopoffReplaySvc, qn: number, rev: number, ops: StopoffOp[]): Promise<void> {
  for (const op of ops) {
    if (op.kind === 'add') await svc.addStopoff(qn, rev, op.payload);
    else if (op.kind === 'update') await svc.updateStopoff(qn, rev, op.id, op.payload);
    else await svc.deleteStopoff(qn, rev, op.id);
  }
}
